import type { ICellRendererParams } from '@mint-lib/ag-grid/community';
import { LoadingCell } from '@mint-lib/ag-grid/components';
import { useTranslation } from '@mint-lib/i18n';
import { Information } from '@mint-lib/icons';
import { useProfile } from '@mint-lib/profile-helpers';
import {
  Chip,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TruncateText,
  typographyVariants,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import { USER_STATUS } from '../../../constants/userTableV2.js';
import type { UserTableV2Row } from '../datasource.js';
import { isUserUpdating, type UserTableV2GridContext } from '../gridContext.js';

type Props = ICellRendererParams<
  UserTableV2Row,
  string | undefined,
  UserTableV2GridContext
>;

export const getUserFullName = (user?: UserTableV2Row) =>
  user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : '';

const NameCell = ({ context, data, value }: Props) => {
  const { t } = useTranslation('@myn/permissions');
  const { profile } = useProfile();
  const isUpdating = isUserUpdating(context, data?.id);

  if (!data || isUpdating) {
    return <LoadingCell />;
  }

  const fullName = value || getUserFullName(data);
  const isCurrentUser = profile?.id === data.id;
  const canViewFfId = profile?.email?.includes('@mint.ai') && data.ffId;
  const isEditDisabled = data.status === USER_STATUS.DeactivatedSystem;

  return (
    <div className="flex h-full min-w-0 items-center gap-2">
      {canViewFfId ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              aria-label="information-icon"
              className="flex shrink-0 cursor-default border-0 bg-transparent p-0"
              type="button"
            >
              <Information className="h-4 w-4 fill-text-secondary" />
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="right"
            sideOffset={8}
            className={cn('max-w-[450px] break-all')}
          >
            {data.ffId}
          </TooltipContent>
        </Tooltip>
      ) : null}
      <TruncateText>
        <button
          type="button"
          disabled={isEditDisabled}
          onClick={() => context?.onEditClick(data)}
          className={cn(
            typographyVariants({
              variant: 'bodyShort01',
              color: isEditDisabled ? 'disabled02' : 'link',
            }),
            'block min-w-0 border-0 bg-transparent p-0 text-left',
            isEditDisabled ? 'cursor-default' : 'cursor-pointer',
          )}
        >
          {fullName}
        </button>
      </TruncateText>
      {isCurrentUser ? (
        <span
          className={cn(
            typographyVariants({ variant: 'caption2', color: 'secondary' }),
            'shrink-0',
          )}
        >
          {t('(you)')}
        </span>
      ) : null}
      {data.agencyUser ? (
        <Chip size="small" variant="outline" color="tertiary">
          {t('Agency')}
        </Chip>
      ) : null}
    </div>
  );
};

export default NameCell;
